import React, { useState, useEffect } from 'react';
import { Copy, ClipboardPaste } from 'lucide-react';
import { hexToRgb, rgbToHex, rgbToHsl, hslToRgb, parseRgb, parseHsl } from './utils';

export const AdvancedColorPicker = ({ value, onChange, disabled }) => {
    const [format, setFormat] = useState('hex');
    const [inputValue, setInputValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    useEffect(() => {
        if (!isFocused) {
            setInputValue(formatColor(value || '#000000', format));
        }
    }, [value, format, isFocused]);

    const formatColor = (hex, fmt) => {
        const rgb = hexToRgb(hex);
        if (!rgb) return hex;
        if (fmt === 'rgb') return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
        if (fmt === 'hsl') {
            const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
            return `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`;
        }
        return hex;
    };
    
    const handleInputChange = (e) => {
        const currentVal = e.target.value;
        setInputValue(currentVal);
        try {
            let newHex = null;
            if (format === 'hex') {
                if (/^#?([0-9a-f]{3}){1,2}$/i.test(currentVal)) {
                    newHex = currentVal.startsWith('#') ? currentVal : `#${currentVal}`;
                }
            } else if (format === 'rgb') {
                const rgb = parseRgb(currentVal);
                if (rgb) newHex = rgbToHex(rgb.r, rgb.g, rgb.b);
            } else if (format === 'hsl') {
                const hsl = parseHsl(currentVal);
                if (hsl) {
                    const rgb = hslToRgb(hsl.h, hsl.s, hsl.l);
                    newHex = rgbToHex(rgb.r, rgb.g, rgb.b);
                }
            }
            if (newHex && newHex.toLowerCase() !== value.toLowerCase()) {
                onChange(newHex);
            }
        } catch (error) {
            // ignore parse errors while typing
        }
    };

    const handleSwatchChange = (e) => {
        onChange(e.target.value);
    };

    const handleCopy = () => {
        if (disabled) return;
        navigator.clipboard.writeText(inputValue);
        setToastMessage('Copied!');
        setTimeout(() => setToastMessage(''), 1500);
    };

    const handlePaste = async () => {
        if (disabled) return;
        try {
            const text = await navigator.clipboard.readText();
            const event = { target: { value: text } };
            handleInputChange(event);
            setToastMessage('Pasted!');
            setTimeout(() => setToastMessage(''), 1500);
        } catch (err) {
            setToastMessage('Paste failed');
            setTimeout(() => setToastMessage(''), 1500);
        }
    };
    
    const formats = ['hex', 'rgb', 'hsl'];

    return (
        <div className="space-y-2">
            <div className="flex items-center gap-2">
                <input
                    type="color"
                    value={value}
                    onChange={handleSwatchChange}
                    disabled={disabled}
                    className="p-0 h-10 w-10 rounded-lg border-none cursor-pointer bg-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <div className="relative flex-grow">
                    <input
                        type="text"
                        value={inputValue}
                        onChange={handleInputChange}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        disabled={disabled}
                        className="w-full pl-3 pr-16 py-2 text-gray-500 bg-transparent outline-none border focus:border-blue-600 shadow-sm rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 disabled:opacity-50"
                    />
                    <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center">
                        <button
                            type="button"
                            onClick={handleCopy}
                            disabled={disabled}
                            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-md disabled:opacity-50"
                            title="Copy color"
                        >
                            <Copy className="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            onClick={handlePaste}
                            disabled={disabled}
                            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-md disabled:opacity-50"
                            title="Paste color"
                        >
                            <ClipboardPaste className="h-4 w-4" />
                        </button>
                    </div>
                    {toastMessage && <div className="absolute right-16 top-1/2 -translate-y-1/2 text-xs bg-gray-700 dark:bg-gray-200 text-white dark:text-black px-2 py-1 rounded-md">{toastMessage}</div>}
                </div>
            </div>
            <div className="flex items-center justify-end gap-1">
                {formats.map(fmt => (
                    <button
                        key={fmt}
                        type="button"
                        onClick={() => setFormat(fmt)}
                        disabled={disabled}
                        className={`px-2 py-1 text-xs font-semibold rounded-md ${
                            format === fmt
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                                : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                        } disabled:opacity-50`}
                    >
                        {fmt.toUpperCase()}
                    </button>
                ))}
            </div>
        </div>
    );
};
